import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Divider,
  Button,
  Chip,
  Avatar,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  TrendingUp,
  AttachMoney,
  Business,
  Receipt,
  TrendingDown,
  Equalizer,
} from "@material-ui/icons";
import { setLoading, setSnackInfo } from "../../redux/actions/appAction";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import HttpService from "../../services/httpService";

// ─── Chart.js Integration ────────────────────────────────────────────────────
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
  },
  header: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3),
    background: "linear-gradient(135deg, #1A3C6E 0%, #2E75B6 100%)",
    borderRadius: theme.spacing(2),
    color: "#fff",
    boxShadow: "0 8px 32px rgba(26, 60, 110, 0.3)",
  },
  headerTitle: {
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  headerSubtitle: {
    opacity: 0.9,
    marginTop: theme.spacing(0.5),
  },
  filterBar: {
    padding: theme.spacing(2),
    backgroundColor: "#fff",
    borderRadius: theme.spacing(1),
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  statCard: {
    padding: theme.spacing(2.5),
    borderRadius: theme.spacing(2),
    backgroundColor: "#fff",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
    },
    height: "100%",
  },
  statIcon: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(1),
  },
  statValue: {
    fontWeight: 700,
    fontSize: "1.75rem",
    color: "#1A3C6E",
  },
  statLabel: {
    color: "#64748b",
    fontSize: "0.875rem",
  },
  chartCard: {
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    backgroundColor: "#fff",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    marginBottom: theme.spacing(3),
  },
  chartTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: "#1A3C6E",
  },
  loadingOverlay: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 400,
  },
  monthChip: {
    margin: theme.spacing(0.5),
    fontWeight: 600,
  },
  monthChipActive: {
    backgroundColor: "#1A3C6E",
    color: "#fff",
  },
  monthChipInactive: {
    backgroundColor: "#e2e8f0",
    color: "#64748b",
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    backgroundColor: "#f1f5f9",
    padding: theme.spacing(1.5),
    textAlign: "left",
    fontWeight: 600,
    color: "#1A3C6E",
    borderBottom: "2px solid #e2e8f0",
  },
  tableCell: {
    padding: theme.spacing(1.5),
    borderBottom: "1px solid #e2e8f0",
  },
  tableRow: {
    "&:hover": {
      backgroundColor: "#f8fafc",
    },
  },
  amountPositive: {
    color: "#059669",
    fontWeight: 600,
  },
  amountNegative: {
    color: "#dc2626",
    fontWeight: 600,
  },
}));

const RentalPaymentDashboardComparison = (props) => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLocalLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [experienceCenterFilter, setExperienceCenterFilter] = useState("all");
  const [experienceCenters, setExperienceCenters] = useState([]);

  // ─── Color Palette ──────────────────────────────────────────────────────
  const COLORS = [
    '#1A3C6E', '#2E75B6', '#4CAF50', '#FF9800', '#F44336',
    '#9C27B0', '#00BCD4', '#8BC34A', '#FF5722', '#795548'
  ];

  // ─── Fetch Dashboard Data ──────────────────────────────────────────────
 
  const fetchDashboardData =  async()=>{
     try {
      setLocalLoading(true);
      props.setLoading(true);
      
      const params = {};
      if (experienceCenterFilter !== "all") {
        params.experience_center_id = experienceCenterFilter;
      }
      
      const [dashboardRes, trendRes] = await Promise.all([
        HttpService.getRentalDashboardData(params),
        HttpService.getRentalTrends(params)
      ]);
      
      if (dashboardRes?.data?.success) {
        setDashboardData(dashboardRes.data.data);
        setExperienceCenters(dashboardRes.data.data.experienceCenters || []);
      }
      
      if (trendRes?.data?.success) {
        setTrendData(trendRes.data.data);
      }
      
      props.setSnackInfo("Dashboard data loaded", "success");
    } catch (err) {
      props.setSnackInfo("Failed to fetch dashboard data", "error");
    } finally {
      setLocalLoading(false);
      props.setLoading(false);
    }
  }

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // ─── Handle submit ──────────────────────────────────────────────────────
  const handleSubmit = () => {
    fetchDashboardData();
  };

  // ─── Format Currency ────────────────────────────────────────────────────
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // ─── Get Chart Data ──────────────────────────────────────────────────────
  const getMonthlyChartData = () => {
    if (!dashboardData?.monthlyData) return null;
    
    const months = dashboardData.monthlyData.map(m => m.display);
    const amounts = dashboardData.monthlyData.map(m => m.totalAmount);
    const counts = dashboardData.monthlyData.map(m => m.totalCount);
    
    return {
      labels: months,
      datasets: [
        {
          label: 'Total Amount (₹)',
          data: amounts,
          backgroundColor: 'rgba(26, 60, 110, 0.6)',
          borderColor: '#1A3C6E',
          borderWidth: 2,
          borderRadius: 4,
          yAxisID: 'y',
        },
        {
          label: 'Number of Payments',
          data: counts,
          backgroundColor: 'rgba(46, 117, 182, 0.6)',
          borderColor: '#2E75B6',
          borderWidth: 2,
          borderRadius: 4,
          yAxisID: 'y1',
        }
      ]
    };
  };

  const getTrendChartData = () => {
    if (!trendData?.chartData) return null;
    
    const labels = trendData.chartData.map(d => d.month);
    const amounts = trendData.chartData.map(d => d.totalAmount);
    
    return {
      labels,
      datasets: [
        {
          label: 'Rental Payments Trend',
          data: amounts,
          fill: true,
          backgroundColor: 'rgba(26, 60, 110, 0.1)',
          borderColor: '#1A3C6E',
          borderWidth: 3,
          tension: 0.4,
          pointBackgroundColor: '#1A3C6E',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 6,
        }
      ]
    };
  };

  const getTopECChartData = () => {
    if (!dashboardData?.summary?.topEC) return null;
    
    const labels = dashboardData.summary.topEC.map(ec => ec.ecName);
    const data = dashboardData.summary.topEC.map(ec => ec.totalAmount);
    
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: COLORS.slice(0, labels.length),
          borderWidth: 2,
          borderColor: '#fff',
        }
      ]
    };
  };

  // ─── Render Stats Cards ──────────────────────────────────────────────────
  const renderStats = () => {
    if (!dashboardData) return null;
    
    const { summary } = dashboardData;
    
    const stats = [
        {
            icon: (
                <Box
                    component="span"
                    style={{
                        fontSize: 24,
                        fontWeight: 700,
                        color: '#059669',
                        fontFamily: 'Arial, sans-serif'
                    }}
                >
                    ₹
                </Box>
            ),
            label: 'Total Rent Paid',
            value: formatCurrency(summary.totalAmount),
            color: '#dcfce7',
        },
      {
        icon: <Receipt style={{ fontSize: 24, color: '#2563eb' }} />,
        label: 'Total Payments',
        value: summary.totalPayments,
        color: '#dbeafe',
      },
      {
        icon: <Business style={{ fontSize: 24, color: '#7c3aed' }} />,
        label: 'Active Experience Centers',
        value: summary.uniqueEC,
        color: '#ede9fe',
      },
      {
        icon: <TrendingUp style={{ fontSize: 24, color: '#f59e0b' }} />,
        label: 'Current Month',
        value: summary.currentMonthDisplay || 'N/A',
        color: '#fef3c7',
      },
    ];
    
    return (
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card className={classes.statCard}>
              <CardContent>
                <div className={classes.statIcon} style={{ backgroundColor: stat.color }}>
                  {stat.icon}
                </div>
                <Typography variant="h5" className={classes.statValue}>
                  {stat.value}
                </Typography>
                <Typography className={classes.statLabel}>
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  // ─── Render Monthly Comparison ──────────────────────────────────────────
  const renderMonthlyComparison = () => {
    if (!dashboardData?.monthlyData) return null;
    
    const months = dashboardData.monthlyData;
    
    return (
      <Card className={classes.chartCard}>
        <Typography variant="h6" className={classes.chartTitle}>
          📊 Monthly Rent Payment Comparison
        </Typography>
        <Box style={{ height: 350 }}>
          {getMonthlyChartData() && (
            <Bar
              data={getMonthlyChartData()}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        let label = context.dataset.label || '';
                        if (label) {
                          label += ': ';
                        }
                        if (context.dataset.label?.includes('Amount')) {
                          label += '₹' + context.raw.toLocaleString();
                        } else {
                          label += context.raw;
                        }
                        return label;
                      }
                    }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: function(value) {
                        return '₹' + value.toLocaleString();
                      }
                    }
                  },
                  y1: {
                    position: 'right',
                    beginAtZero: true,
                    grid: {
                      drawOnChartArea: false,
                    },
                    ticks: {
                      stepSize: 1,
                    }
                  }
                }
              }}
            />
          )}
        </Box>
      </Card>
    );
  };

  // ─── Render Trend Chart ──────────────────────────────────────────────────
  const renderTrendChart = () => {
    if (!trendData?.chartData) return null;
    
    return (
      <Card className={classes.chartCard}>
        <Typography variant="h6" className={classes.chartTitle}>
          📈 Rental Payment Trend (Last {trendData.chartData.length} Months)
        </Typography>
        <Box style={{ height: 300 }}>
          {getTrendChartData() && (
            <Line
              data={getTrendChartData()}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        return '₹' + context.raw.toLocaleString();
                      }
                    }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: function(value) {
                        return '₹' + value.toLocaleString();
                      }
                    }
                  }
                }
              }}
            />
          )}
        </Box>
      </Card>
    );
  };

  // ─── Render Top EC Chart ──────────────────────────────────────────────────
  const renderTopECChart = () => {
    if (!getTopECChartData()) return null;
    
    return (
      <Card className={classes.chartCard}>
        <Typography variant="h6" className={classes.chartTitle}>
          🏢 Top 5 Experience Centers by Rent Payment
        </Typography>
        <Box style={{ height: 300, display: 'flex', justifyContent: 'center' }}>
          <Box style={{ width: '70%', height: '100%' }}>
            <Doughnut
              data={getTopECChartData()}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'right',
                    labels: {
                      font: {
                        size: 12,
                      },
                      padding: 20,
                    }
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = ((context.raw / total) * 100).toFixed(1);
                        return `${context.label}: ₹${context.raw.toLocaleString()} (${percentage}%)`;
                      }
                    }
                  }
                }
              }}
            />
          </Box>
        </Box>
      </Card>
    );
  };

  // ─── Render Detailed Table ────────────────────────────────────────────────
  const renderDetailedTable = () => {
    if (!dashboardData?.monthlyData) return null;
    
    // Get all unique ECs from all months
    const allECs = {};
    dashboardData.monthlyData.forEach(month => {
      month.ecBreakdown.forEach(ec => {
        if (!allECs[ec.ecId]) {
          allECs[ec.ecId] = {
            ecId: ec.ecId,
            ecName: ec.ecName,
            monthlyData: {}
          };
        }
        allECs[ec.ecId].monthlyData[month.paymentMonth] = {
          totalAmount: ec.totalAmount,
          count: ec.count
        };
      });
    });
    
    const ecList = Object.values(allECs);
    const months = dashboardData.monthlyData.map(m => m.paymentMonth);
    
    return (
      <Card className={classes.chartCard}>
        <Typography variant="h6" className={classes.chartTitle}>
          📋 Detailed Monthly Breakdown by Experience Center
        </Typography>
        <div className={classes.tableContainer}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={classes.tableHeader}>Experience Center</th>
                {dashboardData.monthlyData.map((month) => (
                  <th key={month.paymentMonth} className={classes.tableHeader} align="right">
                    {month.display}
                  </th>
                ))}
                <th className={classes.tableHeader} align="right">Total</th>
              </tr>
            </thead>
            <tbody>
              {ecList.map((ec) => {
                let totalAmount = 0;
                return (
                  <tr key={ec.ecId} className={classes.tableRow}>
                    <td className={classes.tableCell} style={{ fontWeight: 600 }}>
                      {ec.ecName}
                    </td>
                    {months.map((month) => {
                      const data = ec.monthlyData[month] || { totalAmount: 0, count: 0 };
                      totalAmount += data.totalAmount;
                      return (
                        <td key={month} className={classes.tableCell} align="right">
                          <Typography variant="body2">
                            ₹{data.totalAmount.toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ({data.count} payments)
                          </Typography>
                        </td>
                      );
                    })}
                    <td className={classes.tableCell} align="right" style={{ fontWeight: 700, color: '#1A3C6E' }}>
                      ₹{totalAmount.toLocaleString()}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ backgroundColor: '#f1f5f9', fontWeight: 700 }}>
                <td className={classes.tableCell} style={{ fontWeight: 700 }}>Total</td>
                {dashboardData.monthlyData.map((month) => (
                  <td key={month.paymentMonth} className={classes.tableCell} align="right" style={{ fontWeight: 700 }}>
                    ₹{month.totalAmount.toLocaleString()}
                  </td>
                ))}
                <td className={classes.tableCell} align="right" style={{ fontWeight: 700, color: '#1A3C6E' }}>
                  ₹{dashboardData.summary.totalAmount.toLocaleString()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    );
  };

  // ─── Loading State ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={classes.loadingOverlay}>
        <CircularProgress />
      </div>
    );
  }

  // ─── Main Render ──────────────────────────────────────────────────────────
  return (
    <div className={classes.root}>
      {/* Header */}
      <Paper className={classes.header}>
        <Typography variant="h4" className={classes.headerTitle}>
          <TrendingUp fontSize="large" />
          Rental Payment Dashboard
        </Typography>
        <Typography variant="body1" className={classes.headerSubtitle}>
          Track and analyze rental payments across experience centers
        </Typography>
      </Paper>

      {/* Filter Bar */}
      <Paper className={classes.filterBar}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
          <InputLabel>Experience Center</InputLabel>
          <Select
            value={experienceCenterFilter}
            onChange={(e) => setExperienceCenterFilter(e.target.value)}
            label="Experience Center"
          >
            <MenuItem value="all">All Centers</MenuItem>
            {experienceCenters.map((ec) => (
              <MenuItem key={ec._id} value={ec._id}>{ec.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          color="primary"
        //   startIcon={<Refresh />}
          onClick={handleSubmit}
          style={{ textTransform: 'none' }}
        >
          Submit
        </Button>

        <Box ml="auto">
          <Chip
            icon={<Equalizer />}
            label={`Last 3 Months`}
            color="primary"
            variant="outlined"
          />
        </Box>
      </Paper>

      {/* Stats Cards */}
      {renderStats()}

      {/* Charts Grid */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          {renderMonthlyComparison()}
        </Grid>
        <Grid item xs={12} lg={4}>
          {renderTopECChart()}
        </Grid>
      </Grid>

      {/* Trend Chart */}
      {renderTrendChart()}

      {/* Detailed Table */}
      {renderDetailedTable()}
    </div>
  );
};

export default connect(null, { setLoading, setSnackInfo })(RentalPaymentDashboardComparison);